import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { API_BASE_URL } from '../config';

const NotificationContext = createContext(null);
const NOTIF_API = `${API_BASE_URL}/v1/notifications`;
const POLL_INTERVAL = 15000; // 15s
// Feature flag: real-time notifications (polling). Disabled for now — flip to true to re-enable.
const REALTIME_NOTIFICATIONS_ENABLED = false;

export function NotificationProvider({ children }) {
  const { token, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const pollRef = useRef(null);

  const hdrs = useCallback(() => ({
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'auth-token': token || '',
  }), [token]);

  const fetchMyNotifications = useCallback(async () => {
    if (!isAuthenticated || !token) return;
    try {
      const res = await fetch(`${NOTIF_API}/my`, { headers: hdrs() });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          setNotifications(data);
          // Mark pending items as delivered
          data.filter(n => n.statusCode === 'pending' || n.statusCode === 'sent').forEach(n => {
            fetch(`${NOTIF_API}/recipients/${n.id}/delivered`, { method: 'POST', headers: hdrs() }).catch(() => {});
          });
        }
      } else if (res.status === 401) {
        setNotifications([]); setUnreadCount(0);
      }
    } catch { /* ignore */ }
  }, [isAuthenticated, token, hdrs]);

  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated || !token) return;
    try {
      const res = await fetch(`${NOTIF_API}/my/unread-count`, { headers: hdrs() });
      if (res.ok) { const data = await res.json(); setUnreadCount(data.count ?? 0); }
    } catch { /* ignore */ }
  }, [isAuthenticated, token, hdrs]);

  const refresh = useCallback(() => {
    fetchMyNotifications();
    fetchUnreadCount();
  }, [fetchMyNotifications, fetchUnreadCount]);

  useEffect(() => {
    if (!isAuthenticated) { setNotifications([]); setUnreadCount(0); return; }
    if (!REALTIME_NOTIFICATIONS_ENABLED) return; // feature disabled — keep code for easy re-enable
    refresh();
    pollRef.current = setInterval(refresh, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [isAuthenticated, refresh]);

  const markRead = useCallback(async (recipientId) => {
    if (!token) return;
    try {
      await fetch(`${NOTIF_API}/recipients/${recipientId}/read`, { method: 'POST', headers: hdrs() });
      setNotifications(prev => prev.map(n => n.id === recipientId ? { ...n, statusId: 4, statusCode: 'read', readAt: new Date().toISOString() } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  }, [token, hdrs]);

  const markAllRead = useCallback(async () => {
    if (!token) return;
    try {
      await fetch(`${NOTIF_API}/my/read-all`, { method: 'POST', headers: hdrs() });
      setNotifications(prev => prev.map(n => ({ ...n, statusId: 4, statusCode: 'read', readAt: new Date().toISOString() })));
      setUnreadCount(0);
    } catch { /* ignore */ }
  }, [token, hdrs]);

  // Admin CRUD
  const createNotification = useCallback(async (payload) => {
    const res = await fetch(`${NOTIF_API}/`, { method: 'POST', headers: hdrs(), body: JSON.stringify(payload) });
    if (!res.ok) { let m = 'Failed'; try { const j = await res.json(); m = j.message || j.error || m; } catch {} throw new Error(m); }
    return res.json();
  }, [hdrs]);

  const getAllNotifications = useCallback(async (createdBy) => {
    const url = createdBy ? `${NOTIF_API}/?createdBy=${createdBy}` : `${NOTIF_API}/`;
    const res = await fetch(url, { headers: hdrs() });
    return res.ok ? res.json() : [];
  }, [hdrs]);

  const deleteNotification = useCallback(async (id) => {
    const res = await fetch(`${NOTIF_API}/${id}`, { method: 'DELETE', headers: hdrs() });
    if (!res.ok) { let m = 'Failed'; try { const j = await res.json(); m = j.message || j.error || m; } catch {} throw new Error(m); }
  }, [hdrs]);

  const getNotificationTypes = useCallback(async () => {
    const res = await fetch(`${NOTIF_API}/types`, { headers: hdrs() });
    return res.ok ? res.json() : [];
  }, [hdrs]);

  const getTargetTypes = useCallback(async () => {
    const res = await fetch(`${NOTIF_API}/target-types`, { headers: hdrs() });
    return res.ok ? res.json() : [];
  }, [hdrs]);

  return (
    <NotificationContext.Provider value={{
      notifications, unreadCount, markRead, markAllRead, refresh,
      createNotification, getAllNotifications, deleteNotification,
      getNotificationTypes, getTargetTypes,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
}
